import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Check, ArrowLeft, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";

const TOPIC_CHOICES = ["Family", "Hobbies", "Memories", "Sports", "Faith", "Current Events", "Gardening", "Cooking", "Pets", "Travel"];
const TIMEZONES = [
  { value: "America/New_York", label: "Eastern (ET)" },
  { value: "America/Chicago", label: "Central (CT)" },
  { value: "America/Denver", label: "Mountain (MT)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
  { value: "America/Phoenix", label: "Arizona" },
  { value: "America/Anchorage", label: "Alaska" },
  { value: "Pacific/Honolulu", label: "Hawaii" },
];
const PLAN_LIST = [
  { id: "starter", name: "Starter", price: 29, tag: "3 calls weekly" },
  { id: "plus", name: "Plus", price: 49, tag: "Daily calls + SMS alerts", popular: true },
  { id: "family", name: "Family", price: 79, tag: "Multiple recipients" },
];

const STEPS = ["Your info", "Loved one", "Schedule", "Topics", "Notifications", "Plan"];

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    user: { name: user?.name || "", mobile: user?.mobile || "" },
    loved_one: {
      name: "", relationship: "Mother", phone: "", timezone_name: "America/New_York",
      family_members: [], preferences: "",
    },
    schedule: { frequency: "daily", days: ["Mon","Tue","Wed","Thu","Fri"], preferred_time: "10:00" },
    topics: ["Family", "Memories"],
    notifications: { email_summaries: true, sms_alerts: true, alert_sensitivity: "medium" },
    plan: "plus",
  });

  const setField = (section, key, val) => setForm((f) => ({ ...f, [section]: { ...f[section], [key]: val } }));
  const toggleTopic = (t) => setForm((f) => ({ ...f, topics: f.topics.includes(t) ? f.topics.filter((x) => x !== t) : [...f.topics, t] }));

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/onboarding/complete", form);
      await refresh();
      toast.success("You're all set! Welcome to CherishLink.");
      track(EVENTS.ONBOARDING_COMPLETED, {
        plan: form.plan,
        topics: (form.topics || []).length,
        frequency: form.schedule?.frequency,
      });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't complete setup");
    } finally {
      setSaving(false);
    }
  };

  const canNext = () => {
    if (step === 0) return form.user.name?.trim().length > 0;
    if (step === 1) return form.loved_one.name && form.loved_one.phone;
    if (step === 3) return form.topics.length > 0;
    return true;
  };

  return (
    <div className="min-h-screen cherishlink-gradient-bg">
      <header className="border-b border-border/50 bg-background/70 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Logo size="lg" />
          <div className="text-sm text-muted-foreground">Step {step + 1} of {STEPS.length}</div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-10 md:py-16">
        <Progress value={((step + 1) / STEPS.length) * 100} className="h-1.5 bg-accent" data-testid="onboarding-progress" />
        <div className="mt-3 flex justify-between text-xs text-muted-foreground">
          {STEPS.map((s, i) => (
            <div key={s} className={`hidden sm:block ${i === step ? "text-foreground font-semibold" : ""}`}>{s}</div>
          ))}
        </div>

        <Card className="mt-10 rounded-3xl border-border/60 soft-shadow">
          <CardContent className="p-8 md:p-10">
            {step === 0 && (
              <StepWrap title="Tell us about you" subtitle="We'll use this to send peace-of-mind summaries.">
                <div>
                  <Label htmlFor="name">Your name</Label>
                  <Input id="name" data-testid="ob-user-name" className="mt-2 h-11 rounded-xl"
                    value={form.user.name} onChange={(e) => setField("user", "name", e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="mobile">Your mobile (for SMS alerts, optional)</Label>
                  <Input id="mobile" data-testid="ob-user-mobile" className="mt-2 h-11 rounded-xl"
                    placeholder="+1 555 123 4567"
                    value={form.user.mobile} onChange={(e) => setField("user", "mobile", e.target.value)} />
                </div>
              </StepWrap>
            )}

            {step === 1 && (
              <StepWrap title="About your loved one" subtitle="Just the basics — you can edit anytime.">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Name</Label>
                    <Input data-testid="ob-lo-name" className="mt-2 h-11 rounded-xl" placeholder="Mary Johnson"
                      value={form.loved_one.name} onChange={(e) => setField("loved_one", "name", e.target.value)} />
                  </div>
                  <div>
                    <Label>Relationship</Label>
                    <Select value={form.loved_one.relationship} onValueChange={(v) => setField("loved_one", "relationship", v)}>
                      <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="ob-lo-relationship">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["Mother","Father","Grandmother","Grandfather","Aunt","Uncle","Spouse","Other"].map((r) => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Phone number</Label>
                  <Input data-testid="ob-lo-phone" className="mt-2 h-11 rounded-xl" placeholder="+1 555 987 6543"
                    value={form.loved_one.phone} onChange={(e) => setField("loved_one", "phone", e.target.value)} />
                </div>
                <div>
                  <Label>Time zone</Label>
                  <Select value={form.loved_one.timezone_name} onValueChange={(v) => setField("loved_one", "timezone_name", v)}>
                    <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="ob-lo-tz"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </StepWrap>
            )}

            {step === 2 && (
              <StepWrap title="Call schedule" subtitle="How often should CherishLink check in?">
                <RadioGroup value={form.schedule.frequency} onValueChange={(v) => setField("schedule", "frequency", v)} className="grid gap-3">
                  {[
                    { id: "daily", label: "Daily", desc: "A warm check-in every day" },
                    { id: "3x_weekly", label: "3× per week", desc: "Monday, Wednesday, Friday" },
                    { id: "custom", label: "Custom days", desc: "You choose which days" },
                  ].map((o) => (
                    <label key={o.id} htmlFor={`freq-${o.id}`} className={`flex gap-3 p-4 rounded-2xl border cursor-pointer transition-colors ${
                      form.schedule.frequency === o.id ? "border-primary bg-accent" : "border-border hover:bg-muted"
                    }`} data-testid={`ob-freq-${o.id}`}>
                      <RadioGroupItem value={o.id} id={`freq-${o.id}`} />
                      <div>
                        <div className="font-semibold">{o.label}</div>
                        <div className="text-sm text-muted-foreground">{o.desc}</div>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
                <div>
                  <Label>Preferred time</Label>
                  <Input type="time" className="mt-2 h-11 rounded-xl" data-testid="ob-time"
                    value={form.schedule.preferred_time} onChange={(e) => setField("schedule", "preferred_time", e.target.value)} />
                </div>
              </StepWrap>
            )}

            {step === 3 && (
              <StepWrap title="Conversation topics" subtitle="What does your loved one enjoy talking about?">
                <div className="flex flex-wrap gap-2">
                  {TOPIC_CHOICES.map((t) => {
                    const active = form.topics.includes(t);
                    return (
                      <button key={t} type="button" onClick={() => toggleTopic(t)}
                        data-testid={`ob-topic-${t.toLowerCase().replace(/ /g, "-")}`}
                        className={`px-4 py-2 rounded-full text-sm border-2 transition-all ${
                          active ? "bg-primary text-primary-foreground border-primary" : "border-border bg-white hover:border-primary/30"
                        }`}>
                        {active && <Check className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />}
                        {t}
                      </button>
                    );
                  })}
                </div>
                <div>
                  <Label htmlFor="prefs">Anything else we should know? (optional)</Label>
                  <Textarea id="prefs" data-testid="ob-preferences" className="mt-2 rounded-xl"
                    placeholder="E.g. hard of hearing in left ear, loves 1960s music, has a cat named Oscar..."
                    value={form.loved_one.preferences}
                    onChange={(e) => setField("loved_one", "preferences", e.target.value)} />
                </div>
              </StepWrap>
            )}

            {step === 4 && (
              <StepWrap title="Notifications" subtitle="How would you like to stay informed?">
                <div className="flex items-center justify-between p-4 rounded-2xl border border-border">
                  <div>
                    <div className="font-semibold">Email summaries</div>
                    <div className="text-sm text-muted-foreground">Short, warm recaps after each call</div>
                  </div>
                  <Switch checked={form.notifications.email_summaries}
                    onCheckedChange={(v) => setField("notifications", "email_summaries", v)}
                    data-testid="ob-notif-email" />
                </div>
                <div className="flex items-center justify-between p-4 rounded-2xl border border-border">
                  <div>
                    <div className="font-semibold">SMS alerts</div>
                    <div className="text-sm text-muted-foreground">Texts if something feels off or calls are missed</div>
                  </div>
                  <Switch checked={form.notifications.sms_alerts}
                    onCheckedChange={(v) => setField("notifications", "sms_alerts", v)}
                    data-testid="ob-notif-sms" />
                </div>
                <div>
                  <Label>Alert sensitivity</Label>
                  <Select value={form.notifications.alert_sensitivity} onValueChange={(v) => setField("notifications", "alert_sensitivity", v)}>
                    <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="ob-notif-sensitivity"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low — only serious concerns</SelectItem>
                      <SelectItem value="medium">Medium — balanced</SelectItem>
                      <SelectItem value="high">High — any mood dips</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </StepWrap>
            )}

            {step === 5 && (
              <StepWrap title="Choose your plan" subtitle="Start with a 7-day free trial. Cancel anytime.">
                <div className="grid gap-3">
                  {PLAN_LIST.map((p) => {
                    const active = form.plan === p.id;
                    return (
                      <button key={p.id} type="button" onClick={() => setForm((f) => ({ ...f, plan: p.id }))}
                        data-testid={`ob-plan-${p.id}`}
                        className={`text-left p-5 rounded-2xl border-2 transition-all ${
                          active ? "border-primary bg-accent" : "border-border hover:border-primary/30 bg-white"
                        }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="font-display text-xl font-semibold">{p.name}</div>
                            {p.popular && <Badge className="bg-secondary text-secondary-foreground">Most Popular</Badge>}
                          </div>
                          <div className="font-display text-2xl font-bold">${p.price}<span className="text-sm text-muted-foreground font-normal">/mo</span></div>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">{p.tag}</div>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-4 p-4 rounded-xl bg-accent/50 text-sm text-accent-foreground flex gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>No credit card needed for the trial. We'll remind you before anything is charged.</span>
                </div>
              </StepWrap>
            )}
          </CardContent>
        </Card>

        <div className="mt-6 flex items-center justify-between">
          <Button variant="ghost" onClick={back} disabled={step === 0} data-testid="ob-back">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={next} disabled={!canNext()} className="rounded-full h-11 px-6" data-testid="ob-next">
              Continue <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={saving} className="rounded-full h-11 px-6" data-testid="ob-finish">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Finish setup"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function StepWrap({ title, subtitle, children }) {
  return (
    <div className="fade-in-up">
      <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">{title}</h2>
      {subtitle && <p className="mt-2 text-muted-foreground">{subtitle}</p>}
      <div className="mt-8 space-y-5">{children}</div>
    </div>
  );
}
